/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/PolicyToggle" />
import React = require("react");
import { ComplexToggle } from "Repos/Views/Settings/Common/Components/ComplexToggle";
import { ISettingsViewService, SettingsTabIds } from "Repos/Views/Settings/Common/Services/SettingsViewService";
import { IPolicy, Scope } from "Repos/Views/Settings/Repositories-Policies/Contracts/Contracts";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import * as Utils from "VSS/Core/Util/String";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { IPoliciesViewService } from "../Services/PoliciesViewService";
import { PolicyInput } from "./PolicyInput";
import { PolicyPanel } from "./PolicyPanel";

interface IPolicyToggleProps {
    goToProject: () => void;
    goToRef?: (ref: string, repositoryId?: string) => void;
    policy: IPolicy;
}

export class PolicyToggle extends VssComponent<IPolicyToggleProps> {
    private disabled: IObservableValue<boolean | undefined>;
    private isPanelShown: IObservableValue<boolean>;
    private isToggled: IObservableValue<boolean>;
    private policy: IObservableValue<IPolicy>;
    private policySvc: IPoliciesViewService;

    public constructor(props: IPolicyToggleProps, context: IVssComponentContext) {
        super(props, context);
        this.policy = new ObservableValue<IPolicy>(this.props.policy);
        this.policySvc = context.pageContext.getService<IPoliciesViewService>("PoliciesViewService");
        this.disabled = new ObservableValue<boolean | undefined>(this.policy.value.disabled || this.policy.value.inheritedValue); // if inherited then we need to disable toggle
        this.isPanelShown = new ObservableValue<boolean>(false);
        this.isToggled = new ObservableValue<boolean>(this.policy.value.value || !!this.policy.value.inheritedValue);
    }

    public render = (): JSX.Element => {
        return (
            <Observer isToggled={this.isToggled} policy={this.policy}>
                {(props: { isToggled: boolean }) => {
                    const complexToggleProps = {
                        description: this.policy.value.description,
                        disabled: this.disabled,
                        id: this.policy.value.type,
                        isToggled: this.isToggled,
                        onToggle: this.onToggle,
                        title: this.policy.value.title,
                        tooltip: this.policy.value.tooltip,
                        learnMoreLink: this.policy.value.link,
                        warningMessage: this.getInheritanceMessage(),
                        warningTitle: this.getInheritanceTitle()
                    };

                    return (
                        <>
                            <ComplexToggle {...complexToggleProps}>
                                {props.isToggled && (
                                    <PolicyInput
                                        key={this.policy.value.id}
                                        className="padding-top-16"
                                        policy={this.policy.value}
                                        onAction={this.onAction}
                                    />
                                )}
                            </ComplexToggle>
                            <PolicyPanel
                                policy={this.policy.value}
                                show={this.isPanelShown}
                                repositoryName={this.policySvc.getSettingsContext().repositoryName}
                                projectName={this.policySvc.getProject().name}
                                goToRef={this.props.goToRef}
                                goToProject={this.props.goToProject}
                            ></PolicyPanel>
                        </>
                    );
                }}
            </Observer>
        );
    };

    private clearLocalPolicy = () => {
        showMessageDialog(this.context.pageContext, {
            lightDismiss: true,
            message: Resources.ConfirmClearMessage,
            okButtonProps: { danger: true, primary: false, text: Resources.Reset },
            title: Resources.ConfirmClear,
            onClose: async result => {
                if (result) {
                    if (this.policy.value.id !== -1) {
                        const ogDisabled = this.disabled.value;
                        this.disabled.value = true;
                        await this.policySvc.deletePolicy(this.policy.value).then(data => {
                            this.processPolicyChange(data, ogDisabled);
                        });
                    }
                }
            }
        });
    };

    private getInheritanceMessage = (): React.ReactNode | undefined => {
        const pol = this.policy.value;
        const additionalPoliciesAtCurrentScopeCount = (pol.additonalCurrentScopePolicies && pol.additonalCurrentScopePolicies.length) || 0;
        const otherPolicyCount =
            ((pol.enterprisePolicies && pol.enterprisePolicies.filter(pol => pol.value).length) || 0) + additionalPoliciesAtCurrentScopeCount;

        const clearLocalPolicy =
            this.policy.value.id !== -1 ? (
                <div className="margin-top-8">
                    <FormatComponent elementType="span" format={Resources.ClearLocalPolicy}>
                        <Link onClick={this.clearLocalPolicy}>{Resources.Here}</Link>
                    </FormatComponent>
                </div>
            ) : null;

        let result: React.ReactNode | undefined = undefined;
        let content: React.ReactNode | undefined = undefined;

        if (pol.scope === Scope.Repository || pol.scope === Scope.Project) {
            if (
                (pol.activeInheritedAndEnterprisePolicies && pol.activeInheritedAndEnterprisePolicies.length) ||
                additionalPoliciesAtCurrentScopeCount
            ) {
                if ((pol.enterprisePolicies || pol.additonalCurrentScopePolicies) && pol.inheritedPolicies) {
                    content = (
                        <span>
                            {Resources.InheritanceMessage} {Resources.ProjectScope}{" "}
                            <Link onClick={this.showPanel}>
                                {this.policySvc.getProject().name}{" "}
                                {otherPolicyCount > 1
                                    ? Utils.format(Resources.InheritanceMessageAdditionalPoliciesPlural, otherPolicyCount)
                                    : Resources.InheritanceMessageAdditionalPoliciesSingle}
                            </Link>
                        </span>
                    );
                } else if (pol.inheritedPolicies) {
                    const settingsSvc = this.context.pageContext.getService<ISettingsViewService>("SettingsViewService");
                    content = (
                        <span>
                            {Resources.InheritanceMessage} {Resources.ProjectScope}{" "}
                            <Link onClick={this.goToProject} href={settingsSvc.getUrl(undefined, undefined, SettingsTabIds.policies)}>
                                {this.policySvc.getProject().name}
                            </Link>
                        </span>
                    );
                } else if (pol.additonalCurrentScopePolicies) {
                    content = (
                        <span>
                            {Resources.InheritanceMessage}
                            <Link onClick={this.showPanel}>
                                {otherPolicyCount
                                    ? Utils.format(Resources.InheritanceMessageBranchPoliciesPlural, otherPolicyCount)
                                    : Resources.InheritanceMessageBranchPoliciesSingle}
                            </Link>
                        </span>
                    );
                } else if (pol.enterprisePolicies) {
                    content = <span>{Resources.EnterpriseManagementMessage}</span>;
                }
            }
        } else if (
            pol.scope &&
            pol.scope >= Scope.Branch &&
            ((pol.activeInheritedAndEnterprisePolicies && pol.activeInheritedAndEnterprisePolicies.length) || additionalPoliciesAtCurrentScopeCount)
        ) {
            const inheritedCount = (pol.inheritedPolicies && pol.inheritedPolicies.filter(pol => pol.value).length) || 0;
            const totalCount = inheritedCount + otherPolicyCount;

            content = (
                <span>
                    {Resources.InheritanceMessage}
                    <Link onClick={this.showPanel}>
                        {totalCount > 1
                            ? Utils.format(Resources.InheritanceMessageBranchPoliciesPlural, totalCount)
                            : Resources.InheritanceMessageBranchPoliciesSingle}
                    </Link>
                </span>
            );
        }

        if (content) {
            result = (
                <div>
                    <div>{content}</div>
                    {clearLocalPolicy}
                </div>
            );
        }

        return result;
    };

    private getInheritanceTitle = (): string | undefined => {
        const pol = this.policy.value;
        const additionalPoliciesAtCurrentScopeCount = (pol.additonalCurrentScopePolicies && pol.additonalCurrentScopePolicies.length) || 0;
        const otherPolicyCount =
            ((pol.enterprisePolicies && pol.enterprisePolicies.filter(pol => pol.value).length) || 0) + additionalPoliciesAtCurrentScopeCount;
        const inheritedCount = (pol.inheritedPolicies && pol.inheritedPolicies.filter(pol => pol.value).length) || 0;
        const totalCount = inheritedCount + otherPolicyCount;

        if (totalCount) {
            return Resources.Inheritance;
        }

        return undefined;
    };

    private goToProject = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>) => {
        if (!event.shiftKey && !event.ctrlKey) {
            this.props.goToProject();
            event.preventDefault();
        }
    };

    private processPolicyChange = (data?: IPolicy, disabledValue?: boolean) => {
        this.disabled.value = disabledValue;

        if (data) {
            this.policy.value = data;
            this.isToggled.value = this.policy.value.value || !!this.policy.value.inheritedValue;
        }
    };

    private onToggle = (value: boolean) => {
        this.isToggled.value = value;
        this.policy.value.value = value;

        this.updatePolicy();
    };

    private onAction = (value?: string, valueObj?: { [key: string]: boolean }) => {
        this.policy.value.settingValue = value;
        this.policy.value.settingValueObject = valueObj;
        this.updatePolicy();
    };

    private showPanel = () => {
        this.isPanelShown.value = true;
    };

    private updatePolicy = () => {
        const ogDisabled = this.disabled.value;
        this.disabled.value = true;

        if (!this.policy.value.value && this.policy.value.id !== -1) {
            // if disabling an existing policy, then delete it
            this.policySvc.deletePolicy(this.policy.value).then(data => {
                this.processPolicyChange(data, ogDisabled);
            });
        } else {
            this.policySvc.savePolicy(this.policy.value).then(data => {
                this.processPolicyChange(data, ogDisabled);
            });
        }
    };
}
